import { Box } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";
import Model from "./Model";
import Painting from "./Painting";
import { useFrame } from "@react-three/fiber";
import GroundLogoPreview from "./GroundLogoPreview";
import FramePreview from "./FramePreview";
import ScreensPreview from "./ScreensPreview";
import WallsGenerator from "./WallsGenerator";
import { FACADES } from "./constants/facades";
import HumanScale from "./HumanScale";
import Events from "./Events";
import {
  updateSelectedTile,
  updatePreviousTiles,
} from "../../../reducers/configurator/configuratorSlice";
import { useDispatch } from "react-redux";
import Themes from "./Themes";
import { handleTileClick } from "../utils/tileUtils";
import { useTranslation } from "react-i18next";
import { GREY_RGB } from "./constants/greyRGB";

const ThreeDimensionsConfiguratorScene = () => {
  const { t } = useTranslation();
  // Import grid data
  const grid = useSelector((state) => state.configurator?.grid);
  // Grid size on Z
  const rowNumber = useSelector((state) => state.configurator?.rowNumber);
  // Grid size on X
  const columnNumber = useSelector((state) => state.configurator?.columnNumber);
  const selectedTile = useSelector((state) => state.configurator?.selectedTile);
  const currentLayout = useSelector((state) => state.configurator?.layout);
  const wallOptions = useSelector(
    (state) => state.configurator?.wallsManagement
  );
  const multimode = useSelector((state) => state.configurator?.multiSelection);
  const lastMainVideo = useSelector(
    (state) => state.configurator.lastMainVideo
  );
  const multiUpload = useSelector((state) => state.configurator.multiUpload);
  const myGrid = useSelector((state) => state.configurator?.grid);
  const isUserAdding = useSelector((state) => state.configurator?.isUserAdding);
  const isUserDeleting = useSelector(
    (state) => state.configurator?.isUserDeleting
  );
  const previousTiles = useSelector(
    (state) => state.configurator?.previousTiles
  );

  const dispatch = useDispatch();

  // Context of animation
  const sceneRef = useRef();

  // ref of objects
  const objectsInSceneRef = useRef({});
  const tileRef = useRef({});
  // const previousTiles = useRef([]);

  const [objectPositions] = useImmer({
    minX: -Math.trunc(columnNumber / 2) || 0,
    minZ: -Math.trunc(rowNumber / 2) || 0,
    maxX: Math.trunc(columnNumber / 2) || 0,
    maxZ: Math.trunc(rowNumber / 2) || 0,
  });

  // useEffect(() => {
  //   console.log(
  //     "X",
  //     columnNumber,
  //     "Z",
  //     rowNumber,
  //     "pos",
  //     objectPositions,
  //     "GRID",
  //     grid
  //   );
  // }, [grid]);

  useFrame(() => {
    Object.values(objectsInSceneRef.current).forEach((item) => {
      if (item.animation > 0) item.el.rotation.y -= item.animation / 250;
    });
  });

  const isOriginalTile = (row, column, startTile) => {
    return !startTile || (row === startTile.row && column === startTile.column);
  };

  const getMiddle3dObjectPosition = (middle) => {
    const xOffset = middle.startTile.column > middle.endTile.column ? -1 : 1;
    const zOffset = middle.startTile.row > middle.endTile.row ? -1 : 1;
    return [
      (middle.configuratorOptions.x / 2) * xOffset,
      0,
      (middle.configuratorOptions.y / 2) * zOffset,
    ];
  };

  const handleClick = (row, col, tile) => {
    dispatch(
      updateSelectedTile({
        row: row,
        column: col,
      })
    );
    handleTileClick(
      tile,
      currentLayout,
      wallOptions,
      multimode,
      lastMainVideo,
      multiUpload,
      myGrid,
      dispatch,
      t
    );
  };

  // Case is highlighted on hover
  const handleOnPointerEnter = (ref) => {
    if (selectedTile === null) {
      ref.el.material.color.set("#D3AF5F");
      ref.el.material.opacity = 1;
    }
    document.body.style.cursor = "pointer";
  };

  // Case is no longer highlighted when not hovered
  const handleOnPointerOut = (ref) => {
    if (selectedTile === null) {
      ref.el.material.color.set("grey");
      ref.el.material.opacity = 0.2;
    }
    document.body.style.cursor = "default";
  };

  // useEffect(() => {
  //   console.log("current layout", currentLayout);
  // }, [currentLayout, selectedTile]);

  // Populate array with selected tiles, if modal is close, empty the array
  useEffect(() => {
    if (selectedTile) {
      // Normal Layout
      if (currentLayout === "normal" && previousTiles.length < 1) {
        dispatch(updatePreviousTiles([{ ...selectedTile }, ...previousTiles]));
      } else {
        dispatch(updatePreviousTiles([{ ...selectedTile }, ...previousTiles]));
      }
    } else {
      updatePreviousTiles([]);
    }
  }, [selectedTile]);

  // Delete array data on layout change
  useEffect(() => {
    updatePreviousTiles([]);
  }, [currentLayout]);

  useEffect(() => {
    if (tileRef.current)
      if (currentLayout === "normal") {
        if (previousTiles.length === 1) {
          tileRef.current[
            `tile-${previousTiles[0].row}-${previousTiles[0].column}`
          ].el.material.opacity = 1;
          tileRef.current[
            `tile-${previousTiles[0].row}-${previousTiles[0].column}`
          ].el.material.color.set("#D3AF5F");
        } else if (previousTiles.length >= 2) {
          tileRef.current[
            `tile-${previousTiles[0].row}-${previousTiles[0].column}`
          ].el.material.opacity = 1;
          tileRef.current[
            `tile-${previousTiles[0].row}-${previousTiles[0].column}`
          ].el.material.color.set("#D3AF5F");
          tileRef.current[
            `tile-${previousTiles[1].row}-${previousTiles[1].column}`
          ].el.material.opacity = 0.2;
          tileRef.current[
            `tile-${previousTiles[1].row}-${previousTiles[1].column}`
          ].el.material.color.set("grey");
        } else {
          Object.keys(tileRef.current).forEach((key) => {
            if (
              tileRef.current[key].el.material.color.r !== GREY_RGB &&
              tileRef.current[key].el.material.color.g !== GREY_RGB &&
              tileRef.current[key].el.material.color.b !== GREY_RGB
            )
              tileRef.current[key].el.material.color.set("grey");
            tileRef.current[key].el.material.opacity = 0.2;
          });
        }
      } else if (currentLayout === "theme" || currentLayout === "event") {
        if (!isUserAdding && !isUserDeleting) {
          if (previousTiles.length === 1) {
            tileRef.current[
              `tile-${previousTiles[0].row}-${previousTiles[0].column}`
            ].el.material.opacity = 1;
            tileRef.current[
              `tile-${previousTiles[0].row}-${previousTiles[0].column}`
            ].el.material.color.set("#D3AF5F");
          } else if (previousTiles.length >= 2) {
            tileRef.current[
              `tile-${previousTiles[0].row}-${previousTiles[0].column}`
            ].el.material.opacity = 1;
            tileRef.current[
              `tile-${previousTiles[0].row}-${previousTiles[0].column}`
            ].el.material.color.set("#D3AF5F");
            tileRef.current[
              `tile-${previousTiles[1].row}-${previousTiles[1].column}`
            ].el.material.opacity = 0.2;
            tileRef.current[
              `tile-${previousTiles[1].row}-${previousTiles[1].column}`
            ].el.material.color.set("grey");
          } else {
            Object.keys(tileRef.current).forEach((key) => {
              if (
                tileRef.current[key].el.material.color.r !== GREY_RGB &&
                tileRef.current[key].el.material.color.g !== GREY_RGB &&
                tileRef.current[key].el.material.color.b !== GREY_RGB
              )
                tileRef.current[key].el.material.color.set("grey");
              tileRef.current[key].el.material.opacity = 0.2;
            });
          }
        } else if (isUserAdding || isUserDeleting) {
          if (previousTiles.length > 0) {
            previousTiles.forEach((tile) => {
              tileRef.current[
                `tile-${tile.row}-${tile.column}`
              ].el.material.opacity = 1;
              tileRef.current[
                `tile-${tile.row}-${tile.column}`
              ].el.material.color.set("#D3AF5F");
            });
          }
        }
      } else {
        if (isUserAdding) {
          if (previousTiles.length > 1) {
            previousTiles.forEach((tile) => {
              tileRef.current[
                `tile-${tile.row}-${tile.column}`
              ].el.material.opacity = 1;
              tileRef.current[
                `tile-${tile.row}-${tile.column}`
              ].el.material.color.set("#D3AF5F");
            });
          }
        } else if (isUserDeleting) {
          previousTiles.forEach((tile) => {
            tileRef.current[
              `tile-${tile.row}-${tile.column}`
            ].el.material.opacity = 0.2;
            tileRef.current[
              `tile-${tile.row}-${tile.column}`
            ].el.material.color.set("grey");
          });
        }
      }
  }, [
    currentLayout,
    selectedTile,
    previousTiles,
    tileRef.current,
    isUserAdding,
    isUserDeleting,
  ]);

  return (
    <>
      {grid.map((row, rowIndex) => {
        return (
          <group key={`row-${rowIndex}`} ref={sceneRef}>
            {row.map((tile, colIndex) => {
              const {
                middle,
                ground,
                north,
                east,
                west,
                south,
                walls,
                event,
                theme,
              } = tile;
              return (
                <group
                  key={`tile-${rowIndex}-${colIndex}`}
                  position={[
                    colIndex + objectPositions.minX,
                    0,
                    rowIndex + objectPositions.minZ,
                  ]}
                >
                  <Box
                    ref={(el) =>
                      (tileRef.current[`tile-${rowIndex}-${colIndex}`] = {
                        el,
                      })
                    }
                    args={[0.95, 0.02, 0.95]}
                    onClick={() => {
                      handleClick(rowIndex, colIndex, tile);
                    }}
                    onPointerEnter={() =>
                      handleOnPointerEnter(
                        tileRef.current[`tile-${rowIndex}-${colIndex}`]
                      )
                    }
                    onPointerOut={() =>
                      handleOnPointerOut(
                        tileRef.current[`tile-${rowIndex}-${colIndex}`]
                      )
                    }
                  >
                    <meshStandardMaterial
                      color="grey"
                      opacity={0.2}
                      transparent
                    />
                  </Box>
                  {middle.link &&
                    isOriginalTile(rowIndex, colIndex, middle.startTile) && (
                      <group
                        ref={(el) =>
                          (objectsInSceneRef.current[
                            `obj-${rowIndex}-${colIndex}-middle`
                          ] = {
                            el,
                            animation: middle.animation,
                          })
                        }
                        position={
                          middle.configuratorOptions
                            ? getMiddle3dObjectPosition(middle)
                            : [0, 0, 0]
                        }
                      >
                        <Model
                          modelUrl={middle.link}
                          config
                          configScale={[
                            middle.customPosition.scale.x,
                            middle.customPosition.scale.y,
                            middle.customPosition.scale.z,
                          ]}
                          configPosition={[
                            -middle.customPosition.position.x,
                            middle.customPosition.position.y,
                            -middle.customPosition.position.z,
                          ]}
                        />
                      </group>
                    )}
                  {middle.imageUrl && (
                    <group
                      ref={(el) =>
                        (objectsInSceneRef.current[
                          `obj-${rowIndex}-${colIndex}-middle`
                        ] = {
                          el,
                          animation: middle.animation,
                        })
                      }
                      position={Object.values(middle.customPosition.position)}
                      rotation={[
                        middle.customPosition.rotation.x,
                        middle.customPosition.rotation.y - 180,
                        middle.customPosition.rotation.z,
                      ]}
                      scale={Object.values(middle.customPosition.scale)}
                    >
                      <Painting
                        imgUrl={middle.imageUrl}
                        isGif={middle.imageUrl.endsWith(".mp4")}
                        molding={middle.molding}
                      />
                    </group>
                  )}
                  {middle.socle && (
                    <group scale-y={1}>
                      <Box args={[0.35, 1, 0.35]} position={[0, 0.5, 0]}>
                        <meshStandardMaterial
                          color={`rgb(${middle.socleColor.r}, ${middle.socleColor.g}, ${middle.socleColor.b})`}
                          transparent
                          opacity={middle.socleColor.a}
                        />
                      </Box>
                    </group>
                  )}
                  {ground.imageUrl && (
                    <GroundLogoPreview
                      imageUrl={ground.imageUrl}
                      scale={ground.scale}
                    />
                  )}
                  {[north, south, east, west].map((facade, facadeIndex) => {
                    if (
                      facade.type === "frame" &&
                      isOriginalTile(rowIndex, colIndex, facade.startTile)
                    )
                      return (
                        <FramePreview
                          key={`frame-${rowIndex}-${colIndex}-${FACADES[facadeIndex]}`}
                          layer={facade}
                          layerName={FACADES[facadeIndex]}
                          imageUrl={facade.previewUrl || facade.imageUrl}
                        />
                      );
                    else if (
                      (facade.type === "screen" || facade.type === "ipad") &&
                      isOriginalTile(rowIndex, colIndex, facade.startTile)
                    ) {
                      return (
                        <ScreensPreview
                          key={`screen-${rowIndex}-${colIndex}-${FACADES[facadeIndex]}`}
                          socle={facade.type === "ipad"}
                          layer={facade}
                          layerName={FACADES[facadeIndex]}
                        />
                      );
                    } else return null;
                  })}
                  {middle.type === "spawn" && <HumanScale config />}
                  <WallsGenerator
                    walls={walls}
                    rowIndex={rowIndex}
                    colIndex={colIndex}
                  />
                  {(event.type === "fw1" || event.type === "fw2") && (
                    <Events name="firework" />
                  )}
                  {event.type === "smoke" && <Events name="smoke" />}
                  {theme.theme === "blackAndWhite" && (
                    <Themes theme="blackAndWhite" radius={theme.radius} />
                  )}
                  {theme.theme === "sepia" && (
                    <Themes theme="sepia" radius={theme.radius} />
                  )}
                  {theme.theme === "rainy" && (
                    <Themes theme="rainy" radius={theme.radius} />
                  )}
                </group>
              );
            })}
          </group>
        );
      })}
    </>
  );
};

export default ThreeDimensionsConfiguratorScene;
